// §4.1 STOCK LOOKUP — floor variant (spec 06 §3.8, §4.1 "Floor behavior: same
// component, bigger touch targets, scan button replaces search focus").
//
// WHAT IS SHARED WITH THE OFFICE AND WHAT IS NOT, and why.
//
// SHARED (imported, not copied): the query tokenizer `lib/lookup-query.ts` (the
// "drop the measure token and search again" rule the empty state depends on),
// the exact-decimal helpers in `lib/measure.ts`, the §7 permission mirror, and
// the `useLookup` data hook. Those are the module's RULES; a second copy on the
// floor would be a second chance to rank stock differently from the office,
// which is exactly the bug §3.8 exists to prevent.
//
// NOT SHARED: the presentation. The office screen is a keyboard-first surface
// (`/` to focus, ↑/↓ to rove, Shift+Enter to reserve) with a dense three-group
// table. None of that survives contact with a glove, and reusing it would ship
// a desktop screen at 360 px and call it mobile. So the floor renders the same
// three §3.8 groups as full-width cards with 3rem targets and a scan-first
// entry — the SAME data, the SAME order, a different body.
//
// §4.1 also narrows the floor role: "floor_worker sees read + Locate only". So
// the card offers OPEN (the unit card, which is where Cut lives and where the
// measure is on screen) and nothing that would mint a reservation.
//
// OFFLINE: lookup is a server-side ranked search over live levels. There is no
// honest offline answer — a cached result set would tell a worker a bobbin is
// available that was cut an hour ago. The screen says so and points at the unit
// card, which DOES cache what it last showed.

import { useState } from "react";
import { Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { FloorEmpty, FloorError, FloorScreen, FloorSkeleton } from "../components/floor-chrome";
import { ScanInput } from "../components/scan-input";
import { useOnline } from "../hooks/use-online";
import { useLookup, type LookupUnitMatch } from "../../office/inventory/hooks/inventory-queries";
import { isEmptyLookup, splitLookupQuery } from "../../office/inventory/lib/lookup-query";
import { formatMeasureWithUom, hoursSince } from "../../office/inventory/lib/measure";
import { inventoryErrorMessage } from "../../office/inventory/lib/errors";
import { RequestError } from "../../../lib/request";
import { enumLabel } from "../../../lib/enum-label";

export function FloorStockScreen() {
  const { t, i18n } = useTranslation();
  const online = useOnline();
  const [text, setText] = useState("");
  const [submitted, setSubmitted] = useState("");
  const [broadened, setBroadened] = useState(false);

  const trimmed = submitted.trim();
  const split = splitLookupQuery(trimmed);
  // The §4.1 "show anyway" relaxation: the SAME shared tokenizer mirror the
  // office uses, so the floor and the office relax the identical token.
  const effective = broadened ? split.withoutMeasure : trimmed;
  // `include_reserved` is sent explicitly in both states — the server defaults
  // it to true, so omitting it makes the control silently do nothing (§4.1 fix
  // round). The floor never hides held stock: a reserved bobbin in the aisle is
  // still the bobbin in the aisle.
  const query = { q: effective, include_reserved: true };
  const lookup = useLookup(query, online && effective.length > 0);

  const error = lookup.error instanceof RequestError ? lookup.error : undefined;
  const data = lookup.data;
  const empty = isEmptyLookup(data);

  return (
    <FloorScreen
      title={t("inventory.floor.stock.title")}
      back={
        <Link to="/floor" className="floor-back">
          {t("inventory.floor.back")}
        </Link>
      }
    >
      <ScanInput
        onScan={(payload) => {
          setText(payload);
          setSubmitted(payload);
          setBroadened(false);
        }}
        label={t("inventory.floor.stock.searchLabel")}
        placeholder={t("inventory.floor.stock.searchPlaceholder")}
      />
      {text.length > 0 && text !== submitted ? (
        <p className="floor-hint">{t("inventory.floor.stock.pressGo")}</p>
      ) : null}

      {!online ? (
        <p className="floor-hint floor-hint-warn" data-testid="floor-stock-offline">
          {t("inventory.floor.stock.offlineBody")}
        </p>
      ) : null}

      {submitted.trim().length === 0 ? (
        <FloorEmpty
          title={t("inventory.floor.stock.promptTitle")}
          body={t("inventory.floor.stock.promptBody")}
        />
      ) : null}

      {online && lookup.isLoading ? <FloorSkeleton rows={3} /> : null}

      {error !== undefined ? (
        <FloorError
          title={t("inventory.floor.stock.errorTitle")}
          detail={inventoryErrorMessage(t, error, "inventory.floor.stock.errorTitle")}
          requestId={error.requestId}
          onRetry={() => {
            void lookup.refetch();
          }}
        />
      ) : null}

      {/* The search space itself was cut (§3.8 `truncated.product_resolution`):
          more products matched the query than were searched, so stock of
          matching products is missing from every group — including from an
          answer that came back empty. Rendered ABOVE the groups and above the
          empty state, because that is the reading it corrupts. */}
      {data?.truncated.product_resolution === true ? (
        <p className="floor-hint floor-hint-warn" data-testid="floor-products-capped">
          {t("inventory.floor.stock.productsCapped")}
        </p>
      ) : null}

      {empty ? (
        <FloorEmpty
          title={t("inventory.floor.stock.noneTitle", { query: effective })}
          body={
            broadened
              ? t("inventory.floor.stock.noneBroadBody")
              : t("inventory.floor.stock.noneBody")
          }
          {...(broadened || split.requiredMeasure === undefined || split.withoutMeasure.length === 0
            ? {}
            : {
                ctaLabel: t("inventory.floor.stock.showAnyway"),
                onCta: () => {
                  setBroadened(true);
                },
              })}
        />
      ) : null}

      {broadened && data !== undefined && !empty ? (
        <p className="floor-hint" data-testid="floor-stock-broadened">
          {t("inventory.floor.stock.broadenedNote")}
        </p>
      ) : null}

      {data !== undefined && data.unit_matches.length > 0 ? (
        <section className="floor-group" data-testid="floor-unit-matches">
          <h2 className="floor-group-head">{t("inventory.floor.stock.groupUnits")}</h2>
          <ul className="floor-cards">
            {data.unit_matches.map((match) => (
              <UnitMatchCard key={match.stock_unit_id} match={match} locale={i18n.language} />
            ))}
          </ul>
          {/* The SERVER says whether the group was cut (§3.8 `truncated`) —
              this used to be `length >= 50`, a copy of a server constant that
              could not see the cap that matters most (product resolution). */}
          {data.truncated.unit_matches ? (
            <p className="floor-hint" data-testid="floor-units-capped">
              {t("inventory.floor.stock.groupCapped")}
            </p>
          ) : null}
        </section>
      ) : null}

      {data !== undefined && data.offcut_combos.length > 0 ? (
        <section className="floor-group" data-testid="floor-offcut-combos">
          <h2 className="floor-group-head">{t("inventory.floor.stock.groupCombos")}</h2>
          <ul className="floor-cards">
            {data.offcut_combos.map((combo, index) => (
              <li key={index} className="floor-card">
                <p className="floor-card-title">
                  {combo.pieces
                    .map((piece) =>
                      formatMeasureWithUom(piece.remaining_measure, split.uom, {
                        locale: i18n.language,
                      }),
                    )
                    .join(" + ")}
                </p>
                <p className="floor-card-sub">
                  {t("inventory.floor.stock.comboTotal", {
                    total: formatMeasureWithUom(combo.combined_measure, split.uom, {
                      locale: i18n.language,
                    }),
                  })}
                </p>
                <ul className="floor-card-pieces">
                  {combo.pieces.map((piece) => (
                    <li key={piece.stock_unit_id}>
                      <Link to="/floor/units/$id" params={{ id: piece.stock_unit_id }}>
                        {piece.serial_code}
                      </Link>{" "}
                      · {piece.location_path ?? t("inventory.common.noLocation")}
                    </li>
                  ))}
                </ul>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {data !== undefined && data.bulk_lots.length > 0 ? (
        <section className="floor-group" data-testid="floor-bulk-lots">
          <h2 className="floor-group-head">{t("inventory.floor.stock.groupBulk")}</h2>
          <ul className="floor-cards">
            {data.bulk_lots.map((row) => (
              <li key={`${row.lot_id}-${row.location_id}`} className="floor-card">
                <p className="floor-card-title">{row.lot_code}</p>
                <p className="floor-card-measure">
                  {formatMeasureWithUom(row.on_hand, row.uom, { locale: i18n.language })}
                </p>
                <p className="floor-card-sub">
                  {row.location_path ?? t("inventory.common.noLocation")}
                </p>
              </li>
            ))}
          </ul>
          {/* The bulk group has the same cap as the unit group and had no note:
              a storekeeper reading 50 lots would take it for the whole shelf
              (§3.8 — no bound is silent, on either surface). */}
          {data.truncated.bulk_lots ? (
            <p className="floor-hint" data-testid="floor-bulk-capped">
              {t("inventory.floor.stock.groupCapped")}
            </p>
          ) : null}
        </section>
      ) : null}

      {/* Module 05's discount pricing is unbound: an EMPTY combos group must
          never read as "there are no combinations", so the degradation the
          server declared is printed rather than dropped (§4.1). */}
      {data?.degraded !== undefined && data.degraded.length > 0 ? (
        <p className="floor-hint" data-testid="floor-stock-degraded">
          {t("inventory.floor.stock.degraded")}
        </p>
      ) : null}
    </FloorScreen>
  );
}

function UnitMatchCard({ match, locale }: { match: LookupUnitMatch; locale: string }) {
  const { t } = useTranslation();
  const hours = hoursSince(match.last_scanned_at);
  return (
    <li className="floor-card" data-testid="floor-unit-card">
      <p className="floor-card-title">{match.serial_code}</p>
      <p className="floor-card-measure">
        {formatMeasureWithUom(match.remaining_measure, match.uom, { locale })}
      </p>
      <p className="floor-card-sub">{match.location_path ?? t("inventory.common.noLocation")}</p>
      <p className="floor-card-meta">
        {hours === undefined
          ? t("inventory.floor.stock.neverScanned")
          : t("inventory.floor.stock.lastSeen", { hours: Math.max(0, Math.round(hours)) })}
      </p>
      {match.lot_qc_status === "quarantined" || match.lot_qc_status === "pending" ? (
        <p className="floor-badge floor-badge-warn" data-testid="floor-card-qc">
          {enumLabel(t, "inventory.vocab.qc", match.lot_qc_status)}
        </p>
      ) : null}
      {match.reserved ? (
        <p className="floor-badge" data-testid="floor-card-reserved">
          {t("inventory.floor.stock.reserved", {
            ref: match.reservation_demand_ref?.demand_type ?? "",
          })}
        </p>
      ) : null}
      <Link
        to="/floor/units/$id"
        params={{ id: match.stock_unit_id }}
        className="floor-go floor-go-lead"
        data-testid="floor-card-open"
      >
        {t("inventory.floor.stock.open")}
      </Link>
    </li>
  );
}
